<script setup lang="ts">
import { ref, watch, onMounted, nextTick } from 'vue'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Check, Code2, AlertCircle } from 'lucide-vue-next'
import mermaid from 'mermaid'
import { torcazaMermaidConfig } from '@/config/mermaidTheme'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  'save': [code: string]
}>()

const code = ref(`flowchart TD
    A[Christmas] -->|Get money| B(Go shopping)
    B --> C{Let me think}
    C -->|One| D[Laptop]
    C -->|Two| E[iPhone]
    C -->|Three| F[fa:fa-car Car]
`)
const svgContent = ref('')
const error = ref('')

let renderTimeout: ReturnType<typeof setTimeout> | null = null

const renderMermaid = async () => {
  if (!code.value.trim()) {
    svgContent.value = ''
    error.value = ''
    return
  }
  
  try {
    // Clear previous errors
    error.value = ''
    
    // Validate syntax first (throws if invalid)
    const isValid = await mermaid.parse(code.value)
    
    if (isValid) {
      // Create a unique id for the render
      const id = `mermaid-render-${Date.now()}`
      const { svg } = await mermaid.render(id, code.value)
      svgContent.value = svg
    }
  } catch (e: any) {
    console.error('Mermaid render error', e)
    error.value = e.message || 'Sintaxis de Mermaid inválida'
  }
}

const onInput = () => {
  if (renderTimeout) {
    clearTimeout(renderTimeout)
  }
  renderTimeout = setTimeout(() => {
    renderMermaid()
  }, 400)
}

watch(() => props.open, (newVal) => {
  if (newVal) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        code.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        renderMermaid();
      }, 300);
      return;
    }
  }

  if (newVal) {
    nextTick(() => {
      mermaid.initialize(torcazaMermaidConfig)
      renderMermaid()
    })
  }
}, { immediate: true })

const handleSave = () => {
  emit('save', code.value)
  emit('update:open', false)
}
</script>

<template>
  <Dialog :open="open" @update:open="$emit('update:open', $event)">
    <DialogContent class="max-w-[90vw] w-[1200px] h-[90vh] flex flex-col p-0 gap-0">
      <DialogHeader class="p-6 pb-4 border-b shrink-0 flex flex-row items-center justify-between">
        <div>
          <DialogTitle class="flex items-center gap-2">
            <Code2 class="w-5 h-5 text-primary" />
            Editor Avanzado Mermaid
          </DialogTitle>
          <DialogDescription>
            Escribe código Mermaid puro en el panel izquierdo y mira el resultado en tiempo real.
          </DialogDescription>
        </div>
        <div class="flex items-center gap-2">
          <Button @click="handleSave">
            <Check class="w-4 h-4 mr-2" />
            Insertar en Documento
          </Button>
        </div>
      </DialogHeader>

      <div class="flex-1 flex flex-col md:flex-row overflow-hidden bg-muted/20">
        <!-- Editor Panel -->
        <div class="w-full md:w-[40%] flex flex-col border-r bg-background">
          <div class="p-2 border-b bg-muted/40 text-sm font-medium text-muted-foreground">
            Código Fuente
          </div>
          <div class="relative flex-1">
            <textarea
              v-model="code"
              @input="onInput"
              class="w-full h-full p-4 resize-none outline-none font-mono text-sm bg-transparent"
              spellcheck="false"
              placeholder="Escribe tu código Mermaid aquí..."
            ></textarea>
            
            <!-- Error overlay -->
            <div 
              v-if="error" 
              class="absolute bottom-0 left-0 right-0 p-3 bg-destructive/10 border-t border-destructive/20 text-destructive text-sm flex items-start gap-2 max-h-[40%] overflow-auto"
            >
              <AlertCircle class="w-4 h-4 mt-0.5 shrink-0" />
              <pre class="whitespace-pre-wrap font-sans text-xs">{{ error }}</pre>
            </div>
          </div>
        </div>

        <!-- Preview Panel -->
        <div class="w-full md:w-[60%] flex flex-col bg-slate-50 dark:bg-slate-900 overflow-hidden">
          <div class="p-2 border-b bg-muted/40 text-sm font-medium text-muted-foreground">
            Previsualización en Vivo
          </div>
          <div class="flex-1 overflow-auto p-4 flex items-center justify-center">
            <div 
              v-if="svgContent" 
              v-html="svgContent" 
              class="max-w-full max-h-full flex items-center justify-center"
              style="width: 100%; height: 100%;"
            ></div>
            <div v-else class="text-muted-foreground flex items-center gap-2">
              <Code2 class="w-8 h-8 opacity-20" />
              <span class="opacity-50">Ingresa código para previsualizar</span>
            </div>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>

<style scoped>
/* Asegurar que el SVG inyectado se ajuste bien */
:deep(svg) {
  max-width: 100%;
  max-height: 100%;
}
</style>
